import React, { useState, useEffect, useRef } from "react";
import { Button, Card, TextField, Image } from "components";
import Label from "egov-ui-kit/utils/translationNode";
import "./index.css";
import logoImage from "egov-ui-kit/assets/images/logoImage.png";

const RESEND_TIMER_SECONDS = 30;

// Simple inline SVG eye icons (open / closed)
const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" stroke="rgba(0,0,0,0.54)" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="3" stroke="rgba(0,0,0,0.54)" strokeWidth="1.5" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.5 18.5 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19M14.12 14.12a3 3 0 1 1-4.24-4.24"
      stroke="rgba(0,0,0,0.54)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.54)" strokeWidth="1.5" />
  </svg>
);

const OTPForm = ({ handleFieldChange, toggleSnackbarAndSetText, form, resendOTP, phoneNumber, logoUrl }) => {
  const fields = form.fields || {};
  const submit = form.submit;
  const { newPassword, confirmnewpassword } = fields || {};

  // Countdown state
  const [secondsLeft, setSecondsLeft] = useState(RESEND_TIMER_SECONDS);
  const intervalRef = useRef(null);

  // Password visibility state
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Start / manage the countdown whenever secondsLeft > 0
  useEffect(() => {
    if (secondsLeft <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // cleanup on unmount / re-run
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft === RESEND_TIMER_SECONDS]); // only (re)start when reset to full value

  const handleResendClick = () => {
    if (secondsLeft > 0) return; // guard: ignore click while counting down
    resendOTP();
    setSecondsLeft(RESEND_TIMER_SECONDS);
  };

  return (
    <Card
      className="user-screens-card forgot-passwd-card col-lg-offset-4 col-lg-4 col-md-offset-4 col-md-4"
      textChildren={
        <div>
          <div className="rainmaker-displayInline" style={{ justifyContent: "center", display: "block", alignItems: "center", marginBottom: "24px" }}>
            <div style={{}}>
              <Image className="mseva-logo" source={logoImage} />
            </div>
            <div style={{ textAlign: "center", marginTop: "20px;" }}>
              <Label bold={true} color="black" fontSize="24px" label="STATE_LABEL" />
            </div>
          </div>
          <Label className="text-center" bold={true} dark={true} fontSize={16} label="CORE_COMMON_FORGOT_PASSWORD_LABEL" />
          <div className="citizen-otp-sent-message" style={{ marginTop: 24, alignItems: "center" }}>
            {/* <Label label="CORE_OTP_SENT_MESSAGE" /> */}
            <Label label="An OTP has been sent to the Email & Mobile Number." />
            <Label label={phoneNumber} />
          </div>
          <Label label="CORE_EMPLOYEE_OTP_CHECK_MESSAGE" color={"rgba(0, 0, 0, 0.3799999952316284)"} fontSize={"12px"} />
          <form>
            <TextField
              errorStyle={{ bottom: "0px" }}
              onChange={(e, value) => handleFieldChange("otpReference", value)}
              id="otp"
              {...fields.otpReference}
              fullWidth={true}
              type={"number"}
            />
            <div style={{ marginBottom: 0 }} className="text-right employee-resend-otp-text">
              {secondsLeft > 0 ? (
                <Label
                  id="otp-trigger"
                  className="otp-prompt"
                  label={`Didn't receive it? Try in ${secondsLeft} Second${secondsLeft !== 1 ? "s" : ""}`}
                />
              ) : (
                <span style={{ cursor: "pointer" }} onClick={handleResendClick}>
                  <Label id="otp-resend" className="otp-resend" label="CORE_OTP_RESEND" />
                </span>
              )}
            </div>
            <TextField onChange={(e, value) => handleFieldChange("username", value)} {...fields.username} />

            {/* New Password field with eye toggle */}
            <div style={{ position: "relative" }}>
              <TextField
                onChange={(e, value) => handleFieldChange("newPassword", value)}
                {...fields.newPassword}
                onKeyDown={(e) => {
                  if (/\s/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                type={showNewPassword ? "text" : "password"}
                fullWidth={true}
              />
              <span
                onClick={() => setShowNewPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "35px",
                  cursor: "pointer",
                }}
              >
                {showNewPassword ? <EyeIcon /> : <EyeOffIcon /> }
              </span>
            </div>

            {/* Confirm Password field with eye toggle */}
            <div style={{ position: "relative" }}>
              <TextField
                style={{ marginBottom: 24 }}
                onKeyDown={(e) => {
                  if (/\s/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e, value) => handleFieldChange("confirmnewpassword", value)}
                {...fields.confirmnewpassword}
                type={showConfirmPassword ? "text" : "password"}
                fullWidth={true}
              />
              <span
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "35px",
                  cursor: "pointer",
                }}
              >
                {showConfirmPassword ? <EyeIcon />  :  <EyeOffIcon />}
              </span>
            </div>

            <Button
              {...submit}
              onClick={(e) => {
                if (newPassword.value !== confirmnewpassword.value) {
                  e.preventDefault();
                  toggleSnackbarAndSetText(
                    true,
                    {
                      labelName: "Password do not match",
                      labelKey: "ERR_PASSWORD_DO_NOT_MATCH",
                    },
                    "error"
                  );
                }
              }}
              fullWidth={true}
              primary={true}
            />
          </form>
        </div>
      }
    />
  );
};

export default OTPForm;



// WEBPACK FOOTER //
// src/modules/employee/User/OTP/components/OTPForm/index.js